/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Injectable, NgZone } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';

import * as localforage from 'localforage';
import * as _ from 'lodash';
import { firebase } from '@app/src/firebase';
import { IMobileAccountCredentials } from '@lib/cordova/account-manager';
import { backgroundModeDisable, backgroundModeEnable } from '@lib/cordova/background-mode';
import { _getDeviceUUID } from '@lib/cordova/device';
import { Store } from '@ngrx/store';
import { IAuthPerson, IAuthResponse } from 'libs/domain';
import { CustomSsoType } from 'libs/domain';
import { MingaPermission, MingaRoleType } from 'libs/domain';
import { sso_pb } from 'libs/generated-grpc-web';
import { date_pb, gateway, legacy_pb, sso_ng_grpc_pb } from 'libs/generated-grpc-web';
import { PersonViewMinimal } from 'libs/generated-grpc-web';
import { IAuthPersonMapper } from 'libs/shared-grpc';
import { MingaMinimalProtoMapper } from 'libs/shared-grpc';
import { dateMessageToDateObject } from 'libs/shared-grpc';
import { fromEvent, Subscription, timer } from 'rxjs';
import { $enum } from 'ts-enum-util';

import { FirebaseAuthUserCredentialMapper } from '@app/src/app/auth/mappers';
import { SaveCancelDialog } from '@app/src/app/dialog/SaveCancel';
import { FirebaseMessaging } from '@app/src/app/firebase/messaging';
import { FtueService } from '@app/src/app/ftue';
import { AnalyticsService } from '@app/src/app/minimal/services/Analytics';
import { AppConfigService } from '@app/src/app/minimal/services/AppConfig';
import { ICreateAccountExtraDetails, ILoginResult } from '@app/src/app/minimal/services/Auth/types';
import { AuthInfoService } from '@app/src/app/minimal/services/AuthInfo';
import { RootService } from '@app/src/app/minimal/services/RootService';
import { SentryService } from '@app/src/app/minimal/services/Sentry/Sentry.service';
import { LandingRoute } from '@app/src/app/modules/landing/constants/landing.constants';
import { PermissionsService } from '@app/src/app/permissions';
import { AppRateService } from '@app/src/app/services/AppRate';
import { MobileAccountManagerService } from '@app/src/app/services/MobileAccountManager';
import { isRosterstreamSSOEnabled } from '@app/src/app/shared/services/auth/sso/sso.utils';
import { platformReload } from '@app/src/app/shared/utils/platform';
import {
  authCleared,
  authCustomSsoError,
  authFail,
  authInit,
  authPersonUpdated,
  authSsoDetached,
  authSsoError,
  authSsoLinked,
  authSsoUnlinked,
  authSuccess,
  authTokenUpdated,
  authUpdated,
  loginSuccess,
} from '@app/src/app/store/AuthStore/actions';
import { setCurrentMinga, setMingaCollection } from '@app/src/app/store/Minga/actions';
import { MingaStoreFacadeService } from '@app/src/app/store/Minga/services';
import { LogoutAction } from '@app/src/app/store/root/rootActions';
import { validateIdTokenRace } from '@app/src/app/util/grpc-multi-invoke';

import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';
import { UserpilotService } from '@shared/services/userpilot/Userpilot.service';

/**
 * @internal
 * readonly common properties between the 3 authentication rpc calls
 */
interface ICommonProtoAuthResponse {
  getStatus(): number | undefined;
  getReason?: () => legacy_pb.StatusReason | undefined;
  getUid(): string;
  getCustomAuthToken(): string;
  hasAccount(): boolean;
  getAccount(): gateway.people_pb.Account | undefined;
  getPersonContentMeta(): gateway.login_pb.PersonContentMeta | undefined;
  getMingaName?: () => string;
  getMingaLogo?: () => string;
  getPersonViewMinimal?: () => PersonViewMinimal;
  getBirthdate(): date_pb.Date | undefined;
  getPublicBirthdate(): boolean;
  getAppConfig(): gateway.connect_pb.AppConfigResponse;
  getAccountRoleType?: () => string;
  getMingaInfo(): gateway.minga_pb.MingaInfoMinimal;
  getParentGroupHashList(): string[];
  getDmPersonalPreference(): boolean;
  getFtueScreensList(): string[];
  getShowRateApp(): boolean;
  getDisabledDm(): boolean;
  getLogins(): number;
  getAccountCreated(): date_pb.Date;
  getUserMingasList(): gateway.minga_pb.MingaInfoMinimal[];
  getEmail(): string;
}

export type CustomOauthParams = {
  token: string;
  provider: CustomSsoType;
};

const generateOAuthStateValue = (numOfChars: number) => {
  const chars = [];
  const allowedChars = '1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  while (numOfChars > 0) {
    const index = Math.floor(Math.random() * allowedChars.length);
    chars.push(allowedChars.charAt(index));
    numOfChars--;
  }
  return chars.join('');
};

const checkOAuthStateValue = async (state: string): Promise<boolean> => {
  const storedValue = await localforage.getItem('OAuthStateValue');
  console.log('checkOAuthStateValue', storedValue, state);
  return storedValue === state;
};

const storeOAuthStateValue = async (state: string) => {
  return await localforage.setItem('OAuthStateValue', state);
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  /** @internal */
  private _detachedSsoCredential: firebase.auth.AuthCredential | null = null;
  private _lastIdToken = '';

  private _tokenExpirationTime: Date | null = null;
  /** How long before token expires do we force a refresh, 2min */
  readonly TOKEN_LEAD_TIME = 120000;
  private _tokenExpireCheckSub: Subscription | null;
  private _customSsoProviderParam;
  private _customSsoTokenParam;

  get detachedSsoCredential(): firebase.auth.AuthCredential | null {
    return this._detachedSsoCredential;
  }

  constructor(
    private _permissions: PermissionsService,
    private _rootService: RootService,
    private _analytics: AnalyticsService,
    private _loginManager: gateway.login_ng_grpc_pb.LoginManager,
    private _firebaseMessaging: FirebaseMessaging,
    private _store: Store<any>,
    private _dialog: MatDialog,
    private _systemAlertSnackBar: SystemAlertSnackBarService,
    private _mobileAccountManager: MobileAccountManagerService,
    private _ftueService: FtueService,
    private _appRateService: AppRateService,
    private _appConfigService: AppConfigService,
    private _ssoService: sso_ng_grpc_pb.SsoManager,
    private _sentryService: SentryService,
    private _mingaStore: MingaStoreFacadeService,
    private _authInfoService: AuthInfoService,
    private _zone: NgZone,
    private _userpilotService: UserpilotService,
    private _route: ActivatedRoute,
    private _router: Router,
  ) {
    /**
     * @deprecated We can remove this listener once we fully migrated to rosterstream SSO
     */
    this._route.queryParams.subscribe(async event => {
      if (event?.provider?.toLowerCase() === 'clever') {
        await this._rootService.addLoadingPromise(this._checkCustomOAuthRedirect(event));
      }
    });

    /**
     * Custom url scheme handler for mobile apps.
     */
    (<any>window).handleOpenURL = async (url: string) => {
      // TODO more debug stuff for deep links
      console.log('handleOpenURL called with url: ', url);
      if (isRosterstreamSSOEnabled(this._appConfigService.getValue('rosterStreamSSOEnabled'))) return;

      /**
       * @deprecated We can remove this legacy handler once we fully migrated to rosterstream SSO
       */
      const legacyDeepLinkHandler = async (url: string) => {
        console.log('legacy deep link handler', url);
        const path = url.split('://')[1];
        const params = path.split('?')[1];
        const paramsObj = params.split('&').reduce((acc, curr) => {
          acc[curr.split('=')[0]] = curr.split('=')[1];
          return acc;
        }, {});

        this._checkCustomOAuthRedirect(paramsObj);
      };

      await this._rootService.addLoadingPromise(legacyDeepLinkHandler(url));
    };
  }

  async startup(): Promise<void> {
    await this._loadCachedPerson().catch(console.error);
    this.startupOnLineAuth();
  }

  /**
   * Function that gets called at app initialization. This method should never
   * throw.
   */
  async startupOnLineAuth() {
    _getDeviceUUID();
    // check for any redirect from our custom oauth providers
    await this._rootService.addLoadingPromise(this._checkCustomOAuthRedirect(this._route.snapshot.queryParams));

    // check if we have had a redirect
    await this._rootService.addLoadingPromise(this._checkRedirectResult());

    // make sure our stored token gets changed every time the token changes.
    firebase.auth().onIdTokenChanged(user => {
      if (user) {
        this._refreshToken(user, false);
      }
    });

    // make sure we are logged in and have a token before moving on and
    // validating auth info.
    await new Promise<void>(resolve => {
      let resolved = false;
      // this gets triggered just when we go from logged in-> logged out
      // and vice versa
      firebase.auth().onAuthStateChanged(async user => {
        if (user) {
          // force a new token in case the one stored was expired.
          // this will trigger onIdTokenChanged above.
          await user.getIdToken(true);
        } else {
          // Only 'clear' the auth if we have already been authenticated before
          if (this._lastIdToken) {
            this._store.dispatch(authCleared());
          }
          // no user, so the auth state has changed to not logged in.
          this._lastIdToken = '';
          this._updatePerson(null);
          await this.clearCachedAuth();

          resolve();
        }

        if (!resolved && this._lastIdToken) {
          resolved = true;
          resolve();
        }
      });
    });

    if (this._lastIdToken) {
      // Fire and forget! We can just use the cached person info until this
      // finishes.
      this.verifyIdToken(this._lastIdToken)
        .then(() => {})
        .catch(console.error);
    }

    if (!window.MINGA_APP_BROWSER) {
      // on resume, ensure the token is checked in case it has expired
      // since we were last in the foreground.
      console.log('ADDING ON RESUME HANDLER');
      fromEvent(document, 'resume').subscribe(event => {
        console.log('GOT A RESUME EVENT', event);
        this._zone.run(() => {
          this._onResumeCheckToken();
        });
      });
    }
  }

  /**
   * @deprecated We can remove this once we fully migrated to rosterstream SSO
   */
  private async _checkCustomOAuthRedirect(params: any): Promise<boolean> {
    const providerParam = params?.provider?.toLowerCase();
    const tokenParam = params?.accessToken;
    const state = params?.state;
    const error = params?.error;
    if (error) {
      this._systemAlertSnackBar.error(error);
      return;
    }
    /*
    if (!state) {
      // no state, might be a link from clever portal, redirect back to clever with state.
      this.signInWithClever();
      return;
    }
    */
    // @TODO: finish checking state value
    if (await checkOAuthStateValue(state)) {
      //console.log('state value doesnt match');
    } else {
      //console.log('state value matches');
    }

    this._customSsoProviderParam = providerParam || this._customSsoProviderParam;
    this._customSsoTokenParam = tokenParam || this._customSsoTokenParam;

    if (!this._customSsoProviderParam || !this._customSsoTokenParam) {
      return true;
    }

    // lets just remove these params from the url
    // now that we have it in local state
    const navigationExtras: NavigationExtras = {
      queryParams: {},
    };
    this._router.navigate([], navigationExtras);

    if (!$enum(CustomSsoType).isValue(this._customSsoProviderParam)) {
      console.warn(`Unsupported custom oauth: ${this._customSsoProviderParam}`);
      return true;
    }

    try {
      if (window.MINGA_APP_ANDROID || (window.MINGA_APP_IOS && (<any>window).cordova.plugins.browsertab)) {
        (<any>window).cordova.plugins.browsertab.close();
      }
      await this.loginWithCustomOauth({
        token: this._customSsoTokenParam,
        provider: this._customSsoProviderParam,
      });
    } catch (err) {
      this._sentryService.captureMessageAsError('There was an issue login into custom sso provider: ' + err.message, {
        request: err,
        provider: this._customSsoProviderParam,
      });
      this._store.dispatch(
        authCustomSsoError({
          customSsoError: {
            message: `There was an issue logging in to ${this._customSsoProviderParam}, please try again`,
          },
        }),
      );
    }

    return true;
  }

  /**
   * @deprecated We can remove this once we fully migrated to rosterstream SSO
   * @returns `true` if call resulted in any store dispatches `false` if
   * nothing happens. Likely due to having no redirect results or results are
   * unhandled.
   */
  private async _checkRedirectResult(): Promise<boolean> {
    let storeDispatched = false;
    const redirectResult = await firebase
      .auth()
      .getRedirectResult()
      .catch(err => {
        if (isRosterstreamSSOEnabled(this._appConfigService.getValue('rosterStreamSSOEnabled'))) return;
        this._sentryService.captureMessageAsError('Got an error checking redirect result ' + err.message, {
          request: err,
        });
        this._store.dispatch(authSsoError({ ssoError: err }));
        storeDispatched = true;
        return null;
      });

    if (isRosterstreamSSOEnabled(this._appConfigService.getValue('rosterStreamSSOEnabled'))) return;

    const ssoProviderInfo =
      redirectResult && (await FirebaseAuthUserCredentialMapper.toISsoProviderInfo(redirectResult));

    if (redirectResult && redirectResult.user && redirectResult.credential) {
      const appUrls = await this._appConfigService.getCountryAppUrls();
      const idToken = await redirectResult.user.getIdToken();
      const raceResult = await validateIdTokenRace(
        {
          idToken,
        },
        appUrls.map(appUrlObj => appUrlObj.apiUrl),
      );

      this._dispatchLoginSuccess(raceResult.response);
      const newAuthProvider = raceResult.response.getNewAuthProvider();
      // user is trying to use SSO using an email that IS in minga, but
      // hasn't actually been linked yet.
      if (newAuthProvider) {
        // Delete the redirect user since the backend told us this is a new auth
        // provider. We're going to link the provider instead.
        await redirectResult.user.delete();
        const customAuthToken = raceResult.response.getCustomAuthToken();
        await firebase.auth().signInWithCustomToken(customAuthToken);
        await firebase.auth().currentUser!.linkWithCredential(redirectResult.credential);

        raceResult.response.setNewAuthProvider(false);
        await this._appConfigService.setAppCountryFromApiUrl(raceResult.host);
        await this.handleAuthResponse(raceResult.response);
        // handleAuthResponse dispatches stuff
        storeDispatched = true;

        if (ssoProviderInfo) {
          this._store.dispatch(authSsoLinked({ ssoProviderInfo }));
        }
      } else if (ssoProviderInfo) {
        //
        if (!raceResult.response.getCustomAuthToken()) {
          // a token wasn't returned, so that means this isn't a current user.
          this._detachedSsoCredential = redirectResult.credential;
          this._store.dispatch(authSsoDetached({ ssoProviderInfo }));
          storeDispatched = true;
        } else {
          // normal sign in via sso.
          this._detachedSsoCredential = null;
          await this._appConfigService.setAppCountryFromApiUrl(raceResult.host);
          await this.handleAuthResponse(raceResult.response);
          // handleAuthResponse dispatches stuff
          storeDispatched = true;
        }
      }
    }

    return storeDispatched;
  }

  /**
   * Discard the current user from firebase. BE CAREFUL using this.
   * Should only really be used in the case of trying to login through SSO
   * when the account doesn't actually exist in minga. In that case, we need
   * to clean up firebase so that the email can be used again.
   */
  async discardCurrentUser() {
    if (this._detachedSsoCredential) {
      await firebase.auth().currentUser?.delete();
      this._store.dispatch(authSsoUnlinked());
      this._detachedSsoCredential = null;
    }
  }

  async linkWithDetachedSSOCredential() {
    if (this._detachedSsoCredential) {
      await firebase.auth().currentUser!.linkWithCredential(this._detachedSsoCredential);
      this._detachedSsoCredential = null;
    }
  }

  public async storeCachedPerson(person: IAuthPerson) {
    await localforage.setItem('cachedPerson', person).catch(console.error);
  }

  private async _loadCachedPerson() {
    await localforage
      .getItem('cachedPerson')
      .then((cachedPerson: any) => this._store.dispatch(authPersonUpdated({ authPerson: cachedPerson })))
      .catch(console.error);
  }

  async clearCachedAuth() {
    await localforage.setItem('lastPersonContentMeta', null);
    await localforage.setItem('cachedPerson', null).catch(console.error);
    await localforage.setItem('cachedToken', null).catch(console.error);
  }

  /**
   * schedule the token refresh so that we force a token refresh before
   * it actually expires, otherwise we may make requests with an expired token
   */
  private _scheduleTokenRefresh() {
    if (this._tokenExpireCheckSub) {
      this._tokenExpireCheckSub.unsubscribe();
    }

    const now = new Date().getTime();
    const expirationDate = this._tokenExpirationTime.getTime();
    const timeout = expirationDate - now - this.TOKEN_LEAD_TIME;

    this._tokenExpireCheckSub = timer(timeout).subscribe(() => {
      this._idTokenExpired();
    });
  }

  private async _refreshToken(
    user: firebase.User,
    forceRefresh: boolean = false,
  ): Promise<firebase.auth.IdTokenResult> {
    // Do not update if we have detached sso credentials. The token will be
    // invalid.
    if (this._detachedSsoCredential) {
      return;
    }
    const token = await user.getIdToken(forceRefresh);
    const hasAuthInited = !!this._lastIdToken;

    const result = await user.getIdTokenResult();

    if (token !== this._lastIdToken) {
      this._tokenExpirationTime = new Date(result.expirationTime);
      this._lastIdToken = token;
      if (this._handleClaims(result.claims)) {
        // if we already had a token, then we are just updating auth
        if (hasAuthInited) {
          this._store.dispatch(authUpdated());
        } else {
          this._store.dispatch(authInit());
        }
      } else {
        this._lastIdToken = '';
      }

      this._scheduleTokenRefresh();
    }
    return result;
  }

  private async _idTokenExpired() {
    // Do not set the last id token with detached sso credentials since the
    // the token will be _invalid_.
    if (this._detachedSsoCredential) {
      console.warn('Not setting lastIdToken due to detached sso credentials');
      return;
    }

    const currentUser = firebase.auth().currentUser;
    // Note: we do not need to keep track of when currentUser is null because
    // onIdTokenChanged in startup takes care of that.

    if (currentUser) {
      await this._refreshToken(currentUser, true);
    }
  }

  getLastIdToken() {
    return this._lastIdToken;
  }

  private _onResumeCheckToken() {
    console.log('on resume triggered by cordova, checking token', this._tokenExpirationTime);
    this.refreshTokenIfExpired();
  }

  /**
   * If token stored is expired (or should be considered expired), then force a
   * refresh from firebase.
   */
  refreshTokenIfExpired() {
    if (this.isTokenExpired()) {
      this._idTokenExpired();
    }
  }

  /**
   * Test if the token is expired (or soon enough to be expired that we should
   * force a refresh)
   */
  isTokenExpired() {
    // if no expiration time is set then it is expired.
    if (!this._tokenExpirationTime) {
      return true;
    }
    const now = new Date().getTime();
    const expirationDate = this._tokenExpirationTime.getTime();
    const timeRemainingBeforeRefreshNeeded = expirationDate - now - this.TOKEN_LEAD_TIME;
    if (timeRemainingBeforeRefreshNeeded > 0) {
      return false;
    }
    return true;
  }

  async signInWithGoogle() {
    const provider = new firebase.auth.GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account',
    });
    await this._signInWithRedirect(provider);
  }

  async signInWithMicrosoft() {
    const provider = new firebase.auth.OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      prompt: 'select_account',
    });
    await this._signInWithRedirect(provider);
  }

  // TODO if we expose this functionality to the user
  // we need to implement a way to handle state parameter
  // functionality to guard agaisnt CSFR -> https://dev.clever.com/docs/il-design#protecting-against-cross-site-request-forgery-csrf
  // eg we need to either keep track of the value in session/db to validate it on oauth callback
  async signInWithClever() {
    const signInHandler = async () => {
      const { clientId, redirectUri, devMode } = await this._appConfigService.getClever();
      this._getStateParam();
      //const url = `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${redirectUri}&client_id=${clientId}&state=${state}&district_id=6442eaf8e5ff8620792beb71`;
      let url = `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${redirectUri}&client_id=${clientId}`;
      if (devMode) {
        url += '&district_id=6442eaf8e5ff8620792beb71';
      }
      if (window.MINGA_APP_ANDROID || (window.MINGA_APP_IOS && (<any>window).cordova.plugins.browsertab)) {
        (<any>window).cordova.plugins.browsertab.openUrl(url, '_blank');
      } else {
        window.location.href = url;
      }
    };

    const checkRedirectHandler = async () => {
      return this._checkCustomOAuthRedirect(this._route.snapshot.queryParams);
    };

    const errorHandler = () => {
      this._store.dispatch(
        authCustomSsoError({
          customSsoError: {
            message: `There was an issue logging in to Clever, please try again`,
          },
        }),
      );
    };

    await this._ssoSignInRedirectHandler(signInHandler, checkRedirectHandler, errorHandler);
  }

  /** @internal */
  private _isProviderConnected(providerId: string) {
    const user = firebase.auth().currentUser;

    if (user) {
      for (const data of user.providerData) {
        if (data && data.providerId === providerId) {
          return true;
        }
      }
    }
    return false;
  }

  /** @internal */
  private async _signInWithRedirect(provider: firebase.auth.AuthProvider) {
    const signInHandler = async () => {
      const currentUser = firebase.auth().currentUser;

      // Link if we're already authenticated and the provider is not already
      // linked. Otherwise just re-login because some firebase operations
      // require the user to be recently signed in.
      if (currentUser && !this._isProviderConnected(provider.providerId)) {
        await currentUser.linkWithRedirect(provider);
      } else {
        await firebase.auth().signInWithRedirect(provider);
      }
    };

    const checkRedirectHandler = async () => {
      return this._checkRedirectResult();
    };

    const errorHandler = err => {
      this._store.dispatch(authSsoError({ ssoError: err }));
    };

    await this._ssoSignInRedirectHandler(signInHandler, checkRedirectHandler, errorHandler);
  }

  /**
   * Helper function to continuously check for redirect
   * since on the hybrid apps it opens in new tab
   */
  private async _ssoSignInRedirectHandler(
    signInHandler: () => Promise<void>,
    checkRedirectHandler: () => Promise<boolean>,
    errorHandler?: (e) => void,
  ) {
    try {
      // seems to be crashing on android 12 phones
      if (!window.MINGA_APP_ANDROID) {
        backgroundModeEnable();
      }

      await signInHandler();

      // Run some recursion that reruns the check redirect functions until true is returns
      // Only happens on hybrid since redirect opens a browser tab
      if (!(await checkRedirectHandler())) {
        // @HACK: Keep checking until we get a result.
        const checkRedirectResultLoop = () => {
          setTimeout(async () => {
            if (!(await checkRedirectHandler())) {
              console.log('checking redirect handler');
              checkRedirectResultLoop();
            }
          }, 1000);
        };

        checkRedirectResultLoop();
      }
    } catch (err) {
      console.log('Got an erroor in redirect', err);
      await this._sentryService.captureMessageAsError('Got an error signing in with redirect ' + err.message, {
        request: err,
      });

      errorHandler?.(err);
    } finally {
      // Regardless of success we want to disable background mode so we don't
      // accidentlly get left in the background and be blamed for bad battery
      // life.
      if (!window.MINGA_APP_ANDROID) {
        backgroundModeDisable();
      }
    }
  }

  /**
   * unlink a minga account from all SSO providers.
   *
   * @returns boolean - whether an sso provider was unlinked or not.
   */
  async unlinkAllAccounts() {
    const ms = this.unLinkAccount(new firebase.auth.OAuthProvider('microsoft.com'));

    const google = await this.unLinkAccount(new firebase.auth.GoogleAuthProvider());

    if (google || ms) {
      await this._ssoService.unlinkAccount(new sso_pb.UnlinkAccountRequest());
    }
    return google || ms;
  }

  /**
   * unlink a minga account from a particular SSO provider
   *
   * @param providerToUnlink
   * @returns boolean - whether the sso provider was unlinked or not.
   */
  async unLinkAccount(
    providerToUnlink: firebase.auth.OAuthProvider | firebase.auth.GoogleAuthProvider,
  ): Promise<boolean> {
    const currentUser = firebase.auth().currentUser;
    if (currentUser?.providerData.find(provider => provider?.providerId === providerToUnlink.providerId)) {
      return currentUser.unlink(providerToUnlink.providerId).then(async () => {
        this._store.dispatch(authSsoUnlinked());
        return true;
      });
    }

    return false;
  }

  async loginWithCustomOauth(config: CustomOauthParams) {
    const appUrls = await this._appConfigService.getCountryAppUrls();

    const raceResult = await validateIdTokenRace(
      {
        customOAuth: config,
      },
      appUrls.map(appUrlObj => appUrlObj.apiUrl),
    );

    const customAuthToken = raceResult.response.getCustomAuthToken();
    const status = raceResult.response.getStatus();

    this._dispatchLoginSuccess(raceResult.response);

    if (status !== legacy_pb.StatusCode.OK || !customAuthToken) {
      throw new Error(`Unable to login to custom sso provider status: ${status} ${!customAuthToken}`);
    }

    await this._appConfigService.setAppCountryFromApiUrl(raceResult.host);
    await this.handleAuthResponse(raceResult.response);
  }

  private async _storeCredentialsOnLogin(email: string, password: string, response: ICommonProtoAuthResponse) {
    if (window.MINGA_APP_IOS && response.getStatus() === legacy_pb.StatusCode.OK) {
      const data = {
        text: 'dialog.credentials.remember',
        saveButtonLocale: 'button.remember',
        cancelButtonLocale: 'button.forget',
      };

      const dialog = this._dialog.open(SaveCancelDialog, { data });
      dialog.afterClosed().subscribe(async result => {
        if (result === true) {
          await this._mobileAccountManager.saveCredentials(email, password);
        } else if (result === false) {
          // user chose not to keep credentials, so let's remove any stored
          await this._mobileAccountManager.removeCredentials();
        }
      });
    }
  }

  async getLoginCredentials(): Promise<IMobileAccountCredentials | null> {
    if ((<any>window).MINGA_APP_IOS) {
      return await this._mobileAccountManager.getCredentials();
    }
    return null;
  }

  async login(email: string, password: string) {
    const loginRequest = new gateway.login_pb.LoginRequest();
    loginRequest.setEmail(email);
    loginRequest.setPassword(password);

    const response = await this._loginManager.login(loginRequest);
    this._dispatchLoginSuccess(response);

    if (response.getStatus() === legacy_pb.StatusCode.OK) {
      this._analytics.logEvent('Login', { type: 'manual', success: 1 });
    } else {
      const err_msg = response.hasReason() ? response.getReason().getMessage() : 'unknown';
      this._analytics.logEvent('Login', {
        type: 'manual',
        success: 0,
        error: err_msg,
      });
    }

    // fire and forget storing credentials in mobile account manager
    await this._storeCredentialsOnLogin(email, password, <ICommonProtoAuthResponse>response);

    return await this.handleAuthResponse(<ICommonProtoAuthResponse>response);
  }

  public async loginToKiosk(email: string, pin: string) {
    const kioskRequest = new gateway.login_pb.KioskLoginRequest();
    kioskRequest.setEmail(email);
    kioskRequest.setPin(pin);
    const response = await this._loginManager.kioskLogin(kioskRequest);
    this._dispatchLoginSuccess(response);
    return await this.handleAuthResponse(<ICommonProtoAuthResponse>response);
  }

  async verifyIdToken(idToken: string) {
    const verifyRequest = new gateway.login_pb.LoginValidationRequest();
    verifyRequest.setIdentityToken(idToken);
    const response = await this._loginManager.validate(verifyRequest);

    if (response.getStatus() === legacy_pb.StatusCode.OK) {
      this._analytics.logEvent('VerifyIdToken', { success: 1 });
    } else {
      this._analytics.logEvent('VerifyIdToken', { success: 0 });
    }

    return await this.handleAuthResponse(<ICommonProtoAuthResponse>response);
  }

  /**
   * Logout with a dialog. Resolves true if user selected yes and has logged out
   * successfully.
   */
  logoutDialog(): Promise<boolean> {
    const data = {
      text: 'dialog.logoutConfirm',
      saveButtonLocale: 'button.logout',
    };

    return new Promise<boolean>((resolve, reject) => {
      const dialog = this._dialog.open(SaveCancelDialog, { data });
      dialog.afterClosed().subscribe(result => {
        if (result === true) {
          const resolveTrue = () => resolve(true);
          this.logout().then(resolveTrue, reject);
        } else {
          resolve(false);
        }
      });
    });
  }

  async logout(opts?: { snackMessage?: string; forceReload?: boolean }) {
    const { snackMessage, forceReload = true } = opts || {};
    const logoutActions = async () => {
      const logoutRequest = new gateway.login_pb.LogoutRequest();
      const deviceId = await this._firebaseMessaging.getDeviceId();
      logoutRequest.setDeviceId(deviceId);
      logoutRequest.setIdentityToken(this._lastIdToken);

      // let other services know we are logging out
      this._store.dispatch(new LogoutAction());

      this._lastIdToken = '';

      await Promise.all([
        this._loginManager.logout(logoutRequest),
        this._firebaseMessaging.deleteCurrentToken(),
        firebase.auth().signOut(),
        this.clearCachedAuth(),
      ]).catch(err => console.error(err));

      this._permissions.flushPermissions();

      await this._router.navigateByUrl('/landing');

      if (snackMessage) {
        this._systemAlertSnackBar.default(snackMessage);
      }
    };

    try {
      await this._rootService.addLoadingPromise(logoutActions());
    } catch (e) {
      console.error('problem logging out', e);
    } finally {
      // lets reload the entire app to reset the all the state, listeners etc...
      // we were running into an bug with root services and observables being destroyed
      if (forceReload) {
        platformReload(window.MINGA_APP_ANDROID || window.MINGA_APP_IOS);
      }
    }
  }

  async forgotPassword(email: string): Promise<gateway.login_pb.ForgotPasswordResponse> {
    const request = new gateway.login_pb.ForgotPasswordRequest();
    request.setEmail(email);

    const response = await this._loginManager.forgotPassword(request);
    return response;
  }

  async resetPassword(resetToken: string): Promise<gateway.login_pb.ResetPasswordResponse> {
    const request = new gateway.login_pb.ResetPasswordRequest();
    request.setResetToken(resetToken);

    const response = await this._loginManager.resetPassword(request);
    return response;
  }

  async createAccountWithResetToken(email: string, password: string, resetToken: string) {
    const createAccountRequest = new gateway.login_pb.CreateAccountRequest();

    createAccountRequest.setEmail(email);
    createAccountRequest.setResetToken(resetToken);
    createAccountRequest.setPassword(password);

    return this._doCreateAccount(createAccountRequest);
  }

  async createAccountWithPin(email: string, password: string, pin: string) {
    const createAccountRequest = new gateway.login_pb.CreateAccountRequest();

    createAccountRequest.setEmail(email);
    createAccountRequest.setPin(pin);
    createAccountRequest.setPassword(password);

    return this._doCreateAccount(createAccountRequest);
  }

  async createAccountWithInviteCode(email: string, inviteCode: string, personDetails: ICreateAccountExtraDetails) {
    const createAccountRequest = new gateway.login_pb.CreateAccountRequest();
    createAccountRequest.setEmail(email);
    createAccountRequest.setMingaInviteCode(inviteCode);
    createAccountRequest.setFirstName(personDetails.firstName);
    createAccountRequest.setLastName(personDetails.lastName);
    createAccountRequest.setPassword(personDetails.password);

    if (personDetails.grade) {
      createAccountRequest.setGrade(personDetails.grade);
    }

    if (personDetails.phoneNumber) {
      createAccountRequest.setPhoneNumber(personDetails.phoneNumber);
    }

    if (personDetails.roleType) {
      createAccountRequest.setRoleType(personDetails.roleType);
    }

    return this._doCreateAccount(createAccountRequest);
  }

  /** @internal */
  private async _doCreateAccount(req: gateway.login_pb.CreateAccountRequest) {
    let detachedSsoCredential = this._detachedSsoCredential;

    if (detachedSsoCredential) {
      const idToken = await firebase.auth().currentUser!.getIdToken();
      req.setDetachedSsoIdToken(idToken);
    }

    const response = await this._loginManager.createAccount(req);

    if (detachedSsoCredential) {
      const user = firebase.auth().currentUser;
      if (user) {
        await user.delete();
      } else {
        console.error('No current user. Unable to use detached SSO credentials to link with new account.');
        detachedSsoCredential = null;
      }
    }

    let authResponse = await this.handleAuthResponse(response as ICommonProtoAuthResponse);

    if (authResponse.success) {
      if (detachedSsoCredential) {
        await firebase.auth().currentUser!.linkWithCredential(detachedSsoCredential);
        // Clear it now that it is no longer needed.
        this._detachedSsoCredential = null;
        // After linking if we don't verify our id token our custom claims are
        // missing.
        authResponse = await this.verifyIdToken(await firebase.auth().currentUser!.getIdToken());
      }

      const email = req.getEmail();
      const password = req.getPassword();

      if (email && password) {
        // fire and forget storing credentials in mobile account manager
        await this._storeCredentialsOnLogin(email, password, <ICommonProtoAuthResponse>response);
      }
    }

    return authResponse;
  }

  private _handleClaims(claims: any) {
    if (!claims || !claims.minga) {
      return false;
    }

    // If we don't have an auth person set we can take the information from
    // our identity token
    if (!this._authInfoService.authPerson) {
      const person: PersonViewMinimal.AsObject | null = _.get(claims, 'minga.person', null);

      if (person) {
        this._store.dispatch(
          authPersonUpdated({
            authPerson: IAuthPersonMapper.fromPersonMinimalAsObject(person),
          }),
        );
      }
    }

    if (typeof claims.minga !== 'object') {
      return false;
    }

    if (Array.isArray(claims.minga.perms)) {
      this._setupPermissions(claims.minga.perms);
    } else {
      this._analytics.logEvent('Auth', {
        error: 'Missing permissions in token claims!',
      });
      console.error('[AUTH] Missing permissions in token claims!');
    }

    this._store.dispatch(authTokenUpdated({ minga: claims.minga }));

    return true;
  }

  async handleAuthResponse(response: ICommonProtoAuthResponse): Promise<ILoginResult> {
    const status = response.getStatus();

    if (status !== legacy_pb.StatusCode.OK) {
      let reason = 'Unknown error. Please try again later';
      let hash = '';
      if (response.getReason) {
        const statusReason = response.getReason();
        if (statusReason) {
          reason = statusReason.getMessage();
          hash = statusReason.getHash();
        }
        this._analytics.logEvent('Auth', { error: reason });
      }

      this._store.dispatch(authFail({}));

      return <ILoginResult>{
        success: false,
        reason,
        hash,
        personName: '',
      };
    }

    const customAuthToken = response.getCustomAuthToken();

    if (customAuthToken) {
      const userCreds = await firebase.auth().signInWithCustomToken(customAuthToken);
      if (userCreds.user) {
        if (userCreds.user.providerData.length > 0) {
          const ssoProviderInfo = await FirebaseAuthUserCredentialMapper.toISsoProviderInfo(userCreds);
          if (ssoProviderInfo) {
            this._store.dispatch(authSsoLinked({ ssoProviderInfo }));
          }
        }
      }
    }

    const usersMingas = (response.getUserMingasList() || []).map(
      MingaMinimalProtoMapper.mingaMinimalProtoToMingaMinimal,
    );

    this._store.dispatch(setMingaCollection({ payload: usersMingas }));

    const mingaInfo = response.getMingaInfo();
    const mingaName = mingaInfo.getMingaName();
    const mingaLogo = mingaInfo.getMingaLogo();
    if (mingaInfo) {
      const mingaMinimal = MingaMinimalProtoMapper.mingaMinimalProtoToMingaMinimal(mingaInfo);
      this._mingaStore.setMingaSettings(mingaMinimal);
      this._analytics.setMingaUserProperties(mingaMinimal);
      this._store.dispatch(setMingaCollection({ payload: [mingaMinimal] }));
      this._store.dispatch(setCurrentMinga({ payload: mingaMinimal }));
    } else {
      this._analytics.logEvent('Auth', {
        error: `No minga info in auth response`,
      });
      console.warn('No minga info in auth response');
    }

    const disableDM = response.getDisabledDm();

    const authResponse: IAuthResponse = {
      dmPersonalPreference: response.getDmPersonalPreference(),
      dmDisabled: disableDM ? disableDM : false,
    };

    this._store.dispatch(authSuccess({ authResponse }));

    if (response.getPersonViewMinimal) {
      const personView = response.getPersonViewMinimal();
      const authPerson = IAuthPersonMapper.fromPersonMinimalProto(personView);
      const email = response.getEmail();
      authPerson.email = email;
      const loginCount = response.getLogins();
      const createdAt = response.getAccountCreated();
      authPerson.loginCount = loginCount;
      authPerson.dateCreated = dateMessageToDateObject(createdAt);
      this._updatePerson(authPerson);
    }

    if (response.getAccountRoleType) {
      // set role for permissions by role
      this._permissions.setMingaRoleType(response.getAccountRoleType());
    }

    const ftueScreens = response.getFtueScreensList();
    await this._ftueService.init(ftueScreens);
    this._appRateService.setShowRateApp(response.getShowRateApp());
    this._userpilotService.identify();

    // Disabling for now b/c it appears it's polluting the web ga data too much
    // this.analytics.sendLoginToWebsiteTracker();

    /*
    userflow.identify(this.person.hash, {
      name: this.person.displayName,
      role: {set: this.person.badgeRoleName, data_type: 'string'}
    });
    */
    /*
    userflow.group(mingaInfo.getMingaHash(), {
      name: mingaInfo.getMingaName(),
    });
    */
    return <ILoginResult>{
      success: true,
      personName: this._authInfoService.authPerson.displayName,
      mingaName: mingaName || '',
      mingaLogo: mingaLogo || '',
    };
  }

  private _setupPermissions(permissionsList: any[]) {
    this._permissions.clearPermissions();
    const perms = permissionsList.filter(p => $enum(MingaPermission).isValue(p));

    $enum(MingaPermission).getEntries();

    if (perms.length !== permissionsList.length) {
      console.warn(
        'Got invalid permissions while setting up auth permissions:',
        permissionsList,
        perms,
        MingaPermission,
      );
    }

    this._permissions.addPermission(perms);
    this._rootService.emitRefreshNavConfig();
  }

  private async _updatePerson(person: IAuthPerson) {
    this.storeCachedPerson(person);
    this._store.dispatch(authPersonUpdated({ authPerson: person }));
  }

  getPersonViewMinimalFromCurrentPerson(): PersonViewMinimal {
    const person = this._authInfoService.authInfo.person;
    const personViewMinimal = new PersonViewMinimal();

    if (person) {
      personViewMinimal.setDisplayName(person.displayName || '');
      personViewMinimal.setPersonHash(person.personHash || '');
      personViewMinimal.setBadgeIconColor(person.badgeIconColor || '');
      personViewMinimal.setBadgeRoleName(person.badgeRoleName || '');
      personViewMinimal.setBadgeIconUrl(person.badgeIconUrl || '');
      personViewMinimal.setProfileImageUrl(person.profileImageUrl || '');
      personViewMinimal.setFirstName(person.firstName || '');
      personViewMinimal.setLastName(person.lastName || '');
    }

    return personViewMinimal;
  }

  authExpiredSnackbar() {
    this._systemAlertSnackBar.error(
      'Woops! We had an issue contacting our servers. Please wait a few seconds and try again!',
    );
  }

  private _getStateParam() {
    const state = generateOAuthStateValue(8);
    storeOAuthStateValue(state);
    return state;
  }

  private _dispatchLoginSuccess(response: ICommonProtoAuthResponse) {
    const status = response.getStatus();
    const role = response.getAccountRoleType() as MingaRoleType;

    if (status === legacy_pb.StatusCode.OK) {
      const usersMingas = (response.getUserMingasList() || []).map(
        MingaMinimalProtoMapper.mingaMinimalProtoToMingaMinimal,
      );
      this._store.dispatch(loginSuccess({ usersMingas, role }));
    }
  }
}
